<template>
  <div class="select">
    <div class="dropdown">
      <Popper
        v-model:open="open"
        :disabled="disabled"
        placement="bottom-end"
      >
        <template #trigger>
          <div class="dropdown__trigger">
            <div class="select__trigger">
              <div class="select__label-wrapper">
                <Loader
                  v-if="loading"
                  class="select__loader"
                />
                <template v-else>
                  <span class="select__label">
                    {{ name }}
                  </span>
                  <span
                    v-if="modelValue.length > 1"
                    class="select__filter-count typo-smallish"
                  >
                    + {{ modelValue.length - 1 }}
                  </span>
                </template>
              </div>
              <div class="select__arrow">
                <UseSvg
                  class="icon icon--small"
                  name="arrow_down_16"
                />
              </div>
            </div>
          </div>
        </template>

        <div class="menu menu--dropdown menu--short menu--with-actions">
          <div class="menu__actions">
            <button
              class="button button--small button--light typo-ignore typo-small"
              type="button"
              @click="clearSelection"
            >
              {{ $t('general.clear_selection') }}
            </button>
            <button
              class="button button--small button--primary typo-ignore typo-small"
              type="submit"
              @click="applySelection"
            >
              {{ $t('general.apply') }}
            </button>
          </div>
          <Loader
            v-if="loading"
            class="menu__loader"
          />
          <div
            v-else
            class="scroll-container menu__items"
          >
            <ul>
              <li
                v-for="inspector in inspectors"
                :key="inspector.id"
                class="menu-item menu-item--dropdown"
              >
                <label class="menu-item__link">
                  <div class="checkbox">
                    <input
                      v-model="inspector.selected"
                      class="checkbox__input"
                      type="checkbox"
                    >
                    <div class="checkbox__label" />
                  </div>
                  <span class="menu-item__label menu-item__label--truncated">{{ inspector.full_name }}</span>
                </label>
              </li>
            </ul>
          </div>
        </div>
      </Popper>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';

import useAjax from '@/functions/use_ajax';

import Popper from '@/components/generic/popper.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import Loader from '@/components/generic/loader.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  disabled: Boolean,
  data: {
    type: Object,
    required: false,
    default: () => ({
      label: I18n.t('site_health_report.all_inspectors'),
      endpoint: null,
    }),
  },
});
const emit = defineEmits(['update:modelValue']);

const route = useRoute();
const ajax = useAjax();

const loading = ref(true);
const inspectors = reactive([]);
const open = ref(false);

const name = computed(() => {
  if (props.modelValue.length === 0) return props.data.label;
  if (loading.value) return '';
  return inspectors.find(x => x.id === props.modelValue[0])?.full_name;
});

onMounted(async () => {
  loading.value = true;
  let endpoint = props.data.endpoint;

  if (!endpoint) endpoint = `operations/${route.params.operationId}`;

  let { ok, data } = await ajax(`/organisations/${route.params.organisationId}/${endpoint}/inspectors`);

  if (ok) {
    inspectors.length = 0;
    inspectors.push(...data);
    if (props.modelValue.length > 0) {
      emit('update:modelValue', props.modelValue.filter(id => data.find(x => x.id === id)));
    }
  }
  loading.value = false;
});

watch(() => props.modelValue, resetSeleted);
watch(open, resetSeleted);

function resetSeleted() {
  if (loading.value) return;
  inspectors.forEach(inspector => {
    inspector.selected = props.modelValue.includes(inspector.id);
  });
}

function selectedIds() {
  if (loading.value) {
    return props.modelValue;
  }
  return inspectors.filter(x => x.selected).map(x => x.id);
}

function applySelection() {
  emit('update:modelValue', selectedIds());
  open.value = false;
}

function clearSelection() {
  if (!loading.value) {
    inspectors.forEach(inspector => {
      inspector.selected = false;
    });
  }
  emit('update:modelValue', []);
  open.value = false;
}
</script>
